import React, { useState } from "react";
import { useReveal } from "../../../hooks/useReveal";
import { submitToWeb3Forms } from "@/lib/web3forms";
import { officeLocations } from "@/data/navigation";

export default function ContactFormSection() {
  const formReveal = useReveal();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setResult(null);

    // Rate Limiting Check (Frontend)
    const lastSubmitTime = localStorage.getItem("teckSolarContactSubmit");
    if (lastSubmitTime) {
      const timeDiff = Date.now() - parseInt(lastSubmitTime, 10);
      const cooldownMs = 60000; // 1 minute cooldown
      if (timeDiff < cooldownMs) {
        const remainingSeconds = Math.ceil((cooldownMs - timeDiff) / 1000);
        setResult({
          success: false,
          message: `Please wait ${remainingSeconds} seconds before submitting another message.`,
        });
        setIsSubmitting(false);
        return;
      }
    }

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(formData.email)) {
      setResult({ success: false, message: "Please enter a valid email address." });
      setIsSubmitting(false);
      return;
    }

    const phoneRegex = /^(\+61|0)?[2-478]\d{8}$/;
    const cleanPhone = formData.phone.replace(/[\s-]/g, "");
    if (!phoneRegex.test(cleanPhone)) {
      setResult({ success: false, message: "Please enter a valid Australian phone number." });
      setIsSubmitting(false);
      return;
    }

    const res = await submitToWeb3Forms(
      {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        service: formData.service || "General Enquiry",
        message: formData.message,
        page: "Teck Solar Contact Page",
      },
      {
        subject: `New Contact Form Message - ${formData.name} (${formData.service || "General Enquiry"})`,
        from_name: "Teck Solar Website",
      },
    );

    setIsSubmitting(false);
    setResult({
      success: res.success,
      message: res.success
        ? "Thank you! Your message has been sent successfully. Our team will contact you shortly."
        : res.message || "Submission error. Please try again.",
    });

    if (res.success) {
      localStorage.setItem("teckSolarContactSubmit", Date.now().toString());
      setFormData({ name: "", email: "", phone: "", service: "", message: "" });
      setTimeout(() => {
        setResult(null);
      }, 3000);
    }
  };

  return (
    <section
      className="py-8 sm:py-8 lg:py-12 px-4 sm:px-6 lg:px-8 bg-[#FAFAFA]"
      ref={formReveal.ref}
    >
      <div className="max-w-7xl mx-auto w-full">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
          {/* Form */}
          <div
            className={`lg:col-span-3 transition-all duration-700 ${formReveal.visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-10"}`}
          >
            <span className="text-primary-500 font-heading font-semibold text-sm tracking-widest uppercase">
              Get In Touch
            </span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-navy-900 mt-3">
              Send Us a Message
            </h2>
            <div className="w-16 h-1 bg-primary-500 rounded-full mt-4 mb-8" />

            {result && (
              <div
                className={`hidden sm:block mb-6 p-4 rounded-xl text-sm font-medium ${
                  result.success
                    ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
                    : "bg-red-50 text-red-800 border border-red-200"
                }`}
              >
                {result.message}
              </div>
            )}

            <form className="space-y-5" onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label
                    htmlFor="contact-name"
                    className="block text-sm font-medium text-navy-700 mb-1.5"
                  >
                    Full Name *
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    placeholder="John Smith"
                    value={formData.name}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^a-zA-Z\s]/g, "");
                      setFormData({ ...formData, name: val });
                    }}
                    className="w-full px-4 py-3 rounded-xl border border-navy-200 text-navy-900 placeholder-navy-400 text-sm transition-all"
                    maxLength={50}
                    minLength={2}
                    pattern="^[a-zA-Z\s]+$"
                    title="Only letters and spaces allowed"
                    required
                  />
                </div>
                <div>
                  <label
                    htmlFor="contact-email"
                    className="block text-sm font-medium text-navy-700 mb-1.5"
                  >
                    Email Address *
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    placeholder="john@example.com"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    className="w-full px-4 py-3 rounded-xl border border-navy-200 text-navy-900 placeholder-navy-400 text-sm transition-all"
                    maxLength={100}
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label
                    htmlFor="contact-phone"
                    className="block text-sm font-medium text-navy-700 mb-1.5"
                  >
                    Phone Number *
                  </label>
                  <input
                    id="contact-phone"
                    name="phone"
                    type="tel"
                    placeholder="0400 000 000"
                    value={formData.phone}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^\d+]/g, "");
                      setFormData({ ...formData, phone: val });
                    }}
                    className="w-full px-4 py-3 rounded-xl border border-navy-200 text-navy-900 placeholder-navy-400 text-sm transition-all"
                    maxLength={15}
                    minLength={8}
                    pattern="^[\d\s\-\+\(\)]+$"
                    title="Please enter a valid phone number"
                    required
                  />
                </div>
                <div>
                  <label
                    htmlFor="contact-service"
                    className="block text-sm font-medium text-navy-700 mb-1.5"
                  >
                    Service Interest
                  </label>
                  <select
                    id="contact-service"
                    name="service"
                    className="w-full px-4 py-3 rounded-xl border border-navy-200 text-navy-900 text-sm transition-all"
                    value={formData.service}
                    onChange={(e) =>
                      setFormData({ ...formData, service: e.target.value })
                    }
                  >
                    <option value="" disabled>
                      Select a service
                    </option>
                    <option value="Residential Solar">Residential Solar</option>
                    <option value="Commercial Solar">Commercial Solar</option>
                    <option value="Battery Storage">Battery Storage</option>
                    <option value="EV Charger">EV Charger</option>
                    <option value="General Enquiry">General Enquiry</option>
                  </select>
                </div>
              </div>
              <div>
                <label
                  htmlFor="contact-message"
                  className="block text-sm font-medium text-navy-700 mb-1.5"
                >
                  Message *
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  rows={5}
                  placeholder="Tell us about your project or enquiry..."
                  value={formData.message}
                  onChange={(e) =>
                    setFormData({ ...formData, message: e.target.value })
                  }
                  className="w-full px-4 py-3 rounded-xl border border-navy-200 text-navy-900 placeholder-navy-400 text-sm resize-none transition-all"
                  maxLength={1000}
                  required
                />
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-lg font-heading font-semibold text-white bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 hover:-translate-y-0.5 shadow-md shadow-primary-500/25 transition-all text-base disabled:opacity-75 cursor-pointer"
              >
                {isSubmitting ? "Sending Message..." : "Send Message"}
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                  />
                </svg>
              </button>
              
              {result && (
                <div
                  className={`block sm:hidden mt-4 p-4 rounded-xl text-sm font-medium animate-fade-in ${
                    result.success
                      ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
                      : "bg-red-50 text-red-800 border border-red-200"
                  }`}
                >
                  {result.message}
                </div>
              )}
            </form>
          </div>

          {/* Info Sidebar */}
          <div
            className={`lg:col-span-2 transition-all duration-700 delay-200 ${formReveal.visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-10"}`}
          >
            <div className="bg-navy-900 rounded-2xl p-8 text-white sticky top-28">
              <h3 className="text-xl font-heading font-semibold mb-6">
                Contact Information
              </h3>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-primary-500/20 flex items-center justify-center shrink-0 mt-0.5">
                    <svg
                      className="w-5 h-5 text-primary-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                  </div>
                  <div className="space-y-2">
                    <p className="text-navy-400 text-xs font-semibold uppercase tracking-wider">
                      Our Offices
                    </p>
                    <div className="space-y-2 text-xs leading-relaxed">
                      {officeLocations.map((loc) => (
                        <div key={loc.id} className="leading-snug">
                          <span className="text-primary-400 font-semibold">{loc.name}: </span>
                          <span className="text-white/80">{loc.address}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 rounded-xl bg-primary-500/20 flex items-center justify-center shrink-0">
                    <svg
                      className="w-5 h-5 text-primary-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="text-navy-400 text-xs font-semibold uppercase tracking-wider">Call Us</p>
                    <a
                      href="tel:+611300134077"
                      className="text-white font-medium hover:text-primary-400 transition-colors"
                    >
                      +61 1300 134 077
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-primary-500/20 flex items-center justify-center shrink-0 mt-0.5">
                    <svg
                      className="w-5 h-5 text-primary-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="text-navy-400 text-xs font-semibold uppercase tracking-wider">Email Us</p>
                    <div className="space-y-1 mt-1">
                      <a
                        href="mailto:info@teck-solar.com.au"
                        className="text-white font-medium hover:text-primary-400 transition-colors block text-sm"
                      >
                        info@teck-solar.com.au                         
                      </a>
                      <a
                        href="mailto:sales@tecksolar.com.au"
                        className="text-white font-medium hover:text-primary-400 transition-colors block text-sm">
                        sales@tecksolar.com.au</a>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 rounded-xl bg-primary-500/20 flex items-center justify-center shrink-0">
                    <svg
                      className="w-5 h-5 text-primary-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="text-navy-400 text-xs">Business Hours</p>
                    <p className="text-white font-medium text-sm">
                      Mon - Fri: 8am - 5pm
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-8 pt-6 border-t border-navy-700">
                <p className="text-sm text-navy-400 mb-4">Follow Us</p>
                <div className="flex items-center gap-3">
                  <a
                    href="#"
                    className="w-10 h-10 rounded-full bg-navy-800 hover:bg-primary-500 flex items-center justify-center transition-all duration-300"
                    aria-label="Facebook"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    </svg>
                  </a>
                  <a
                    href="#"
                    className="w-10 h-10 rounded-full bg-navy-800 hover:bg-primary-500 flex items-center justify-center transition-all duration-300"
                    aria-label="Instagram"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                    </svg>
                  </a>
                  <a
                    href="#"
                    className="w-10 h-10 rounded-full bg-navy-800 hover:bg-primary-500 flex items-center justify-center transition-all duration-300"
                    aria-label="LinkedIn"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
